// Bulk Upload Stops — wizard screen. Two modes:
//  - Create stops: pick an EMPTY tour -> sheet + audio zips + images zip -> validation
//    report -> mapping confirm screen -> sequential import with progress/resume -> report.
//  - Add audio languages: pick a tour that ALREADY has its stops -> sheet (English tab
//    identifies the stops) + the new language zips -> plan (which stop gets which
//    language) -> upload + merge onto the existing stops -> report.
// Uses only endpoints the panel already calls; publishing remains a manual step in Edit Tour.

import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { importApi, AuthExpiredError, PermissionDeniedError } from "./importApi";
import {
  preflight,
  runImport,
  verifyTour,
  preflightAddLanguages,
  runAddLanguages,
  Progress,
  RunResult,
  AddLangPlan,
  AddLangResult,
} from "./runner";

// @ts-ignore — untyped shared JS engine
import { parseSheet } from "./engine/parseSheet";
// @ts-ignore
import { parseAudioZip, parseImagesZip } from "./engine/parseZips";
// @ts-ignore
import { validateAll } from "./engine/validate";
// @ts-ignore
import { validateAddLanguages } from "./engine/addLanguages";

type Step = "files" | "confirm" | "running" | "paused" | "done";
type Mode = "create" | "add-languages";

interface Issue {
  severity: "blocker" | "warning" | "info";
  code: string;
  message: string;
  sl?: number;
  lang?: string;
}

const SEV_STYLE: Record<string, string> = {
  blocker: "text-red-600",
  warning: "text-amber-600",
  info: "text-gray-500",
};
const SEV_ICON: Record<string, string> = { blocker: "⛔", warning: "⚠️", info: "ℹ️" };

const btn = "bg-blue text-white rounded-full py-2.5 px-6 font-roboto font-normal disabled:opacity-40 disabled:cursor-not-allowed";
const btnGhost = "border border-blue text-blue rounded-full py-2.5 px-6 font-roboto font-normal";

export default function TourImport() {
  const navigate = useNavigate();
  const { adminPermission } = useSelector((state: any) => state.counter);
  const singleTour = useSelector((state: any) => state.singleTourSlice);

  const [mode, setMode] = useState<Mode>("create");
  const [step, setStep] = useState<Step>("files");
  const [tours, setTours] = useState<any[]>([]);
  const [tourId, setTourId] = useState<number | "">(singleTour?.tour?.id ?? "");
  const [sheetFile, setSheetFile] = useState<File | null>(null);
  const [audioFiles, setAudioFiles] = useState<File[]>([]);
  const [imagesFile, setImagesFile] = useState<File | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [issues, setIssues] = useState<Issue[]>([]);
  const [tourInfo, setTourInfo] = useState<any>(null);
  const [plan, setPlan] = useState<AddLangPlan | null>(null);
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [progress, setProgress] = useState<Progress | null>(null);
  const [logLines, setLogLines] = useState<string[]>([]);
  const [result, setResult] = useState<RunResult | null>(null);
  const [addResult, setAddResult] = useState<AddLangResult | null>(null);
  const [runError, setRunError] = useState<string | null>(null);
  const [audioPreview, setAudioPreview] = useState<{ sl: number; lang: string; url: string } | null>(null);
  const [expandedSl, setExpandedSl] = useState<number | null>(null);

  const modelRef = useRef<any>(null); // parsed stops incl. lazy zip data — not React state (big)
  const thumbsRef = useRef<Record<number, string>>({});
  const abortRef = useRef(false);

  useEffect(() => {
    importApi.getAllTours().then((all) => setTours((all || []).filter((t: any) => !t.isDeleted))).catch(() => toast.error("Could not load tours list"));
    return () => {
      Object.values(thumbsRef.current).forEach((u) => URL.revokeObjectURL(u));
      if (audioPreview) URL.revokeObjectURL(audioPreview.url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Warn before leaving mid-run (an interrupted run is resumable, but warn anyway)
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (step === "running") {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [step]);

  if (adminPermission?.is_admin == 2 && !adminPermission?.tours) {
    return (
      <h1 className="text-3xl font-openSans font-bold text-aqua items-center flex justify-center h-[80vh]">
        You don't have permission
      </h1>
    );
  }

  const blockers = issues.filter((i) => i.severity === "blocker");
  const isAdd = mode === "add-languages";

  function switchMode(m: Mode) {
    if (m === mode) return;
    setMode(m);
    setIssues([]);
    setPlan(null);
    setTourInfo(null);
    setResult(null);
    setAddResult(null);
    setImagesFile(null);
    modelRef.current = null;
  }

  async function readInputs() {
    const sheetBuf = new Uint8Array(await sheetFile!.arrayBuffer());
    const zipIssues: Issue[] = [];
    const audioZips = [] as any[];
    for (const f of audioFiles) {
      setBusy(`Reading ${f.name}…`);
      audioZips.push(parseAudioZip({ name: f.name, data: new Uint8Array(await f.arrayBuffer()) }, zipIssues));
    }
    setBusy("Validating…");
    const sheet = parseSheet(sheetBuf);
    return { sheet, audioZips, zipIssues };
  }

  async function handleValidate() {
    if (!tourId) return toast.error("Pick the target tour first");
    if (!sheetFile) return toast.error("Upload the stops spreadsheet (.xlsx)");
    if (!audioFiles.length) return toast.error("Upload at least one language audio zip");
    if (!isAdd && !imagesFile) return toast.error("Upload the stop-images zip (1.jpg = stop 1, …)");
    setBusy("Reading files…");
    try {
      if (isAdd) {
        const { sheet, audioZips, zipIssues } = await readInputs();
        const validation = validateAddLanguages(sheet, audioZips, zipIssues);
        modelRef.current = validation.model;
        setBusy("Checking the target tour…");
        const pf = await preflightAddLanguages(Number(tourId), validation.model);
        setTourInfo(pf.tour);
        setPlan(pf.plan);
        setIssues([...validation.issues, ...pf.issues]);
      } else {
        const { sheet, audioZips, zipIssues } = await readInputs();
        setBusy(`Reading ${imagesFile!.name}…`);
        const imagesZip = parseImagesZip({ name: imagesFile!.name, data: new Uint8Array(await imagesFile!.arrayBuffer()) }, zipIssues);
        setBusy("Validating…");
        const validation = validateAll(sheet, audioZips, imagesZip, zipIssues);
        modelRef.current = validation.model;

        setBusy("Checking the target tour…");
        const pf = await preflight(Number(tourId), validation.model);
        setTourInfo(pf.tour);
        setIssues([...validation.issues, ...pf.issues]);

        // build image thumbnails for the confirm table
        Object.values(thumbsRef.current).forEach((u) => URL.revokeObjectURL(u));
        thumbsRef.current = {};
        for (const s of validation.model.stops) {
          if (s.image?.data) {
            thumbsRef.current[s.sl] = URL.createObjectURL(new Blob([s.image.data], { type: s.image.ext === "png" ? "image/png" : "image/jpeg" }));
          }
        }
      }
      setConfirmChecked(false);
      setStep("confirm");
    } catch (e: any) {
      toast.error(`Could not read the files: ${e?.message || e}`);
    } finally {
      setBusy(null);
    }
  }

  function playAudio(stop: any, lang: string) {
    try {
      if (audioPreview) URL.revokeObjectURL(audioPreview.url);
      const data = stop.audio[lang].getData();
      const url = URL.createObjectURL(new Blob([data], { type: "audio/mpeg" }));
      setAudioPreview({ sl: stop.sl, lang, url });
    } catch (e: any) {
      toast.error(`Could not extract audio: ${e?.message || e}`);
    }
  }

  async function startRun() {
    setStep("running");
    setRunError(null);
    abortRef.current = false;
    setLogLines([]);
    const onProgress = (p: Progress) => {
      setProgress(p);
      if (p.detail && !p.uploadPct) setLogLines((prev) => [...prev.slice(-200), `${p.stopSl ? `Stop ${p.stopSl}: ` : ""}${p.detail}`]);
    };
    try {
      if (isAdd) {
        const res = await runAddLanguages(modelRef.current, Number(tourId), onProgress, () => abortRef.current);
        setAddResult(res);
      } else {
        const res = await runImport(modelRef.current, Number(tourId), onProgress, () => abortRef.current);
        setResult(res);
      }
      setStep("done");
    } catch (e: any) {
      if (e instanceof AuthExpiredError) {
        setRunError(`${e.message} Your progress is saved — log in again in another tab, then press Resume.`);
      } else if (e instanceof PermissionDeniedError) {
        setRunError(e.message);
      } else if (String(e?.message || "").includes("Paused by operator")) {
        setRunError(null);
      } else if (isAdd) {
        setRunError(`Run stopped: ${e?.message || e}. Stops already updated keep their new languages. Press Resume to continue — uploaded files are reused and languages already present are skipped.`);
      } else {
        setRunError(`Import stopped: ${e?.message || e}. Nothing is visible to users (tour unpublished). Press Resume to continue — finished uploads and stops are skipped.`);
      }
      setStep("paused");
    }
  }

  const model = modelRef.current;
  const audioLangs: string[] = model?.audioLangs || [];
  const textLangs: string[] = model?.languages || [];
  const verifyLangs: string[] = isAdd ? addResult?.langs || audioLangs : audioLangs;

  const tourOptionLabel = (t: any) => {
    let name = "?";
    try {
      const n = typeof t.name === "string" ? JSON.parse(t.name) : t.name;
      name = (n || []).find((x: any) => x.language === "en")?.text || n?.[0]?.text || "?";
    } catch {}
    return `#${t.id} — ${name} ${t.published === 1 ? "(PUBLISHED)" : "(unpublished)"}`;
  };

  return (
    <div className="w-full px-12 py-6">
      <div className="flex items-center justify-between">
        <p className="text-blue font-roboto font-bold text-xl">Bulk Upload Stops</p>
        {step !== "running" && (
          <button className={btnGhost} onClick={() => navigate("/stopList")}>Back to Stop List</button>
        )}
      </div>

      {/* ---------------- STEP: files ---------------- */}
      {step === "files" && (
        <div className="mt-6 max-w-3xl">
          <div className="flex gap-6 mb-5 font-roboto" role="radiogroup" aria-label="Mode">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name="mode" checked={mode === "create"} onChange={() => switchMode("create")} />
              <span><b>Create stops</b> — for a tour with no stops yet (sheet + audio zips + images zip)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name="mode" checked={mode === "add-languages"} onChange={() => switchMode("add-languages")} />
              <span><b>Add audio languages</b> — for a tour that already has its stops (sheet + the new language zips)</span>
            </label>
          </div>

          <label className="block font-roboto text-blue mb-1">
            {isAdd ? "Target tour (already has its stops)" : "Target tour (must have no stops yet)"}
          </label>
          <select className="border rounded-lg p-2 w-full" value={tourId} onChange={(e) => setTourId(Number(e.target.value) || "")}>
            <option value="">— pick a tour —</option>
            {tours.map((t: any) => (
              <option key={t.id} value={t.id}>{tourOptionLabel(t)}</option>
            ))}
          </select>

          <div className="mt-5 space-y-4">
            <div>
              <label className="block font-roboto text-blue mb-1">
                {isAdd
                  ? "Stops spreadsheet (.xlsx) — the English tab (names + coordinates) identifies the existing stops; stop text is NOT changed"
                  : "Stops spreadsheet (.xlsx — one tab per language, English tab has coordinates)"}
              </label>
              <input type="file" accept=".xlsx" onChange={(e) => setSheetFile(e.target.files?.[0] || null)} />
            </div>
            <div>
              <label className="block font-roboto text-blue mb-1">
                {isAdd
                  ? 'Audio zips for the languages to ADD — one per language, named like "Spanish Audio.zip" (languages a stop already has are skipped)'
                  : 'Audio zips — one per language, named like "English Audio.zip" (upload the ones you have; add the rest later with "Add audio languages")'}
              </label>
              <input type="file" accept=".zip" multiple onChange={(e) => setAudioFiles(Array.from(e.target.files || []))} />
              {audioFiles.length > 0 && <p className="text-sm text-gray-600 mt-1">{audioFiles.map((f) => f.name).join(", ")}</p>}
            </div>
            {!isAdd && (
              <div>
                <label className="block font-roboto text-blue mb-1">Stop images zip (1.jpg = stop 1, 2.jpg = stop 2, … — one per stop, max 5 MB each)</label>
                <input type="file" accept=".zip" onChange={(e) => setImagesFile(e.target.files?.[0] || null)} />
              </div>
            )}
          </div>

          <button className={`${btn} mt-6`} disabled={!!busy} onClick={handleValidate}>
            {busy || "Validate & Preview"}
          </button>
        </div>
      )}

      {/* ---------------- STEP: confirm (create) ---------------- */}
      {step === "confirm" && model && !isAdd && (
        <div className="mt-4">
          <div className={`rounded-xl p-4 ${blockers.length ? "bg-red-50" : "bg-green-50"}`}>
            <p className="font-roboto font-bold">
              {blockers.length ? `⛔ ${blockers.length} problem(s) must be fixed before import` : "✅ Ready to import"}
              {" — "}{model.stops.length} stops · text in {textLangs.join(", ")} · audio in {audioLangs.join(", ") || "none"}
              {tourInfo && <> · into tour #{tourInfo.id}</>}
            </p>
            {(["blocker", "warning", "info"] as const).map((sev) =>
              issues.filter((i) => i.severity === sev).map((i, k) => (
                <p key={sev + k} className={`text-sm mt-1 ${SEV_STYLE[sev]}`}>{SEV_ICON[sev]} {i.message}</p>
              ))
            )}
          </div>

          <table className="w-full mt-4 text-sm font-roboto">
            <thead>
              <tr className="text-left text-blue border-b">
                <th className="py-2 pr-2">SL</th>
                <th className="pr-2">Image</th>
                <th className="pr-2">Stop name (click for all languages)</th>
                <th className="pr-2">Audio</th>
                <th className="pr-2">Lock</th>
                <th className="pr-2">Hidden</th>
                <th>Location / radius</th>
              </tr>
            </thead>
            <tbody>
              {model.stops.map((s: any) => (
                <>
                  <tr key={s.sl} className="border-b align-top">
                    <td className="py-2 pr-2 font-bold">{s.sl}</td>
                    <td className="pr-2">
                      {thumbsRef.current[s.sl]
                        ? <img src={thumbsRef.current[s.sl]} alt="" className="w-16 h-16 object-cover rounded-lg" />
                        : <span className="text-red-600">missing</span>}
                    </td>
                    <td className="pr-2 cursor-pointer" onClick={() => setExpandedSl(expandedSl === s.sl ? null : s.sl)}>
                      <span className="font-semibold">{s.names.en}</span>
                      <p className="text-gray-500 max-w-md truncate">{s.descriptions.en}</p>
                    </td>
                    <td className="pr-2 whitespace-nowrap">
                      {audioLangs.map((l) => (
                        <button key={l} className={`mr-1 px-2 py-0.5 rounded-full border ${s.audio[l] ? "border-blue text-blue" : "border-red-400 text-red-500"}`}
                          onClick={() => s.audio[l] && playAudio(s, l)} title={s.audio[l]?.entryName || "missing"}>
                          {l} ▸
                        </button>
                      ))}
                      {audioPreview && audioPreview.sl === s.sl && (
                        <div className="mt-1"><audio controls autoPlay src={audioPreview.url} style={{ height: 28 }} /></div>
                      )}
                    </td>
                    <td className="pr-2">{s.audioLock ? "🔒 locked" : "free"}</td>
                    <td className="pr-2">{s.hideStop ? "yes" : "—"}</td>
                    <td className="whitespace-nowrap">{s.lat?.toFixed(5)}, {s.lng?.toFixed(5)} · {s.radius} m</td>
                  </tr>
                  {expandedSl === s.sl && (
                    <tr key={`x${s.sl}`} className="border-b bg-gray-50">
                      <td />
                      <td colSpan={6} className="py-2">
                        {textLangs.map((l) => (
                          <p key={l} className="text-xs mb-1"><b className="uppercase">{l}</b>: {s.names[l]} — <span className="text-gray-600">{s.descriptions[l]}</span></p>
                        ))}
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>

          <div className="mt-5 flex items-center gap-4">
            <button className={btnGhost} onClick={() => setStep("files")}>← Change files</button>
            <label className="flex items-center gap-2 font-roboto">
              <input type="checkbox" checked={confirmChecked} onChange={(e) => setConfirmChecked(e.target.checked)} />
              I reviewed the mapping above — names, images, audio and locations are correct
            </label>
            <button className={btn} disabled={!!blockers.length || !confirmChecked} onClick={startRun}>
              Start Import ({model.stops.length} stops)
            </button>
          </div>
        </div>
      )}

      {/* ---------------- STEP: confirm (add languages) ---------------- */}
      {step === "confirm" && model && isAdd && (
        <div className="mt-4">
          <div className={`rounded-xl p-4 ${blockers.length ? "bg-red-50" : "bg-green-50"}`}>
            <p className="font-roboto font-bold">
              {blockers.length ? `⛔ ${blockers.length} problem(s) must be fixed first` : "✅ Ready to add languages"}
              {plan && (
                <>
                  {" — "}{plan.totals.stops} stop(s) to update · {plan.totals.uploads} file(s) to upload ({audioLangs.join(", ") || "none"})
                  {plan.totals.skipped > 0 && <> · {plan.totals.skipped} already present</>}
                </>
              )}
              {tourInfo && <> · tour #{tourInfo.id}{tourInfo.published === 1 ? " (LIVE)" : ""}</>}
            </p>
            {(["blocker", "warning", "info"] as const).map((sev) =>
              issues.filter((i) => i.severity === sev).map((i, k) => (
                <p key={sev + k} className={`text-sm mt-1 ${SEV_STYLE[sev]}`}>{SEV_ICON[sev]} {i.message}</p>
              ))
            )}
          </div>

          {plan && (
            <table className="w-full mt-4 text-sm font-roboto">
              <thead>
                <tr className="text-left text-blue border-b">
                  <th className="py-2 pr-2">SL</th>
                  <th className="pr-2">Stop (sheet)</th>
                  <th className="pr-2">Existing stop on server</th>
                  <th className="pr-2">Has audio</th>
                  <th className="pr-2">Will add (click to play)</th>
                  <th>Location</th>
                </tr>
              </thead>
              <tbody>
                {plan.rows.map((r) => (
                  <tr key={r.sl} className="border-b align-top">
                    <td className="py-2 pr-2 font-bold">{r.sl}</td>
                    <td className="pr-2 font-semibold">{r.stop.names.en}</td>
                    <td className="pr-2">
                      #{r.serverId} {r.serverName}
                      {r.matchedBy === "location" && (
                        <span className="ml-2 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs" title="The English name differs from the sheet; matched by coordinates. The server name is NOT changed.">
                          matched by location
                        </span>
                      )}
                    </td>
                    <td className="pr-2 whitespace-nowrap">
                      {r.existing.length ? r.existing.map((l) => (
                        <span key={l} className="mr-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">{l}</span>
                      )) : <span className="text-gray-400">none</span>}
                    </td>
                    <td className="pr-2 whitespace-nowrap">
                      {r.add.length ? r.add.map((l) => (
                        <button key={l} className={`mr-1 px-2 py-0.5 rounded-full border ${r.stop.audio[l] ? "border-blue text-blue" : "border-red-400 text-red-500"}`}
                          onClick={() => r.stop.audio[l] && playAudio(r.stop, l)} title={r.stop.audio[l]?.entryName || "missing"}>
                          {l} ▸
                        </button>
                      )) : <span className="text-gray-400">nothing — already present</span>}
                      {audioPreview && audioPreview.sl === r.sl && (
                        <div className="mt-1"><audio controls autoPlay src={audioPreview.url} style={{ height: 28 }} /></div>
                      )}
                    </td>
                    <td className="whitespace-nowrap">{r.stop.lat?.toFixed(5)}, {r.stop.lng?.toFixed(5)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="mt-5 flex items-center gap-4">
            <button className={btnGhost} onClick={() => setStep("files")}>← Change files</button>
            <label className="flex items-center gap-2 font-roboto">
              <input type="checkbox" checked={confirmChecked} onChange={(e) => setConfirmChecked(e.target.checked)} />
              I reviewed the plan above — each sheet stop is matched to the right existing stop
            </label>
            <button className={btn} disabled={!!blockers.length || !confirmChecked || !plan} onClick={startRun}>
              Add languages ({plan?.totals.uploads ?? 0} files)
            </button>
          </div>
        </div>
      )}

      {/* ---------------- STEP: running / paused ---------------- */}
      {(step === "running" || step === "paused") && (
        <div className="mt-6 max-w-3xl">
          <div className="bg-amber-50 rounded-xl p-3 font-roboto text-amber-800">
            ⚠️ Do not edit this tour in the admin (and keep this tab open) until the report appears.
          </div>
          <div className="mt-4">
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div className="bg-aqua h-3 rounded-full transition-all" style={{ width: `${progress ? Math.round((progress.stopsDone / Math.max(progress.stopsTotal, 1)) * 100) : 0}%` }} />
            </div>
            <p className="font-roboto mt-2">
              {progress ? `${progress.stopsDone}/${progress.stopsTotal} stops` : "Starting…"}
              {progress?.stopName && <> — {progress.stopName}</>}
              {progress?.detail && <>: {progress.detail}</>}
              {progress?.uploadPct != null && <> {progress.uploadPct}%</>}
            </p>
          </div>
          {step === "running" ? (
            <button className={`${btnGhost} mt-4`} onClick={() => { abortRef.current = true; }}>Pause after current file</button>
          ) : (
            <div className="mt-4">
              {runError && <p className="text-red-600 font-roboto mb-3">{runError}</p>}
              <button className={btn} onClick={startRun}>Resume</button>
            </div>
          )}
          <div className="mt-4 bg-gray-50 rounded-xl p-3 max-h-64 overflow-y-auto text-xs font-mono">
            {logLines.map((l, i) => <div key={i}>{l}</div>)}
          </div>
        </div>
      )}

      {/* ---------------- STEP: done (create) ---------------- */}
      {step === "done" && !isAdd && result && (
        <div className="mt-6 max-w-3xl">
          <div className={`rounded-xl p-4 ${result.verify?.ok ? "bg-green-50" : "bg-red-50"}`}>
            <p className="font-roboto font-bold text-lg">
              {result.verify?.ok ? "✅ Import finished and verified" : "⛔ Import finished but verification FAILED — do not publish"}
            </p>
            <p className="font-roboto mt-1">
              Created {result.created.length} stops (skipped {result.skipped.length} already existing) · {result.uploads} files uploaded ·
              route {result.tail.route ? "✓" : "✗"} · detail file {result.tail.detail ? "✓" : "✗"} · offline bump {result.tail.offline ? "✓" : "✗"}
            </p>
            {result.verify && (
              <p className="font-roboto mt-1">
                CDN check: {result.verify.checked}/{result.verify.totalFiles} manifest files reachable.
                {result.verify.failed.length > 0 && result.verify.failedNonTranscribe.length === 0 &&
                  ` ${result.verify.failed.length} transcript file(s) still generating — normal, they arrive in the background.`}
              </p>
            )}
            {result.verify && result.verify.failedNonTranscribe.length > 0 && (
              <div className="mt-2 text-sm text-red-700 font-mono">
                {result.verify.failedNonTranscribe.map((u) => <div key={u}>✗ {u}</div>)}
              </div>
            )}
          </div>
          <div className="mt-4 font-roboto">
            <p className="font-bold">Next steps:</p>
            <ol className="list-decimal ml-6 mt-1 space-y-1">
              <li>Open the Stop List and spot-check a few stops (names, images, audio playback per language).</li>
              <li>Check the map route on the tour.</li>
              <li>Transcripts keep generating in the background — re-run verification later if you want to confirm them.</li>
              <li>When happy, set Published = True in Edit Tour. Publishing is never done by the importer.</li>
            </ol>
          </div>
        </div>
      )}

      {/* ---------------- STEP: done (add languages) ---------------- */}
      {step === "done" && isAdd && addResult && (
        <div className="mt-6 max-w-3xl">
          <div className={`rounded-xl p-4 ${addResult.verify?.ok ? "bg-green-50" : "bg-red-50"}`}>
            <p className="font-roboto font-bold text-lg">
              {addResult.verify?.ok ? "✅ Languages added and verified" : "⛔ Languages added but verification FAILED — check the files below"}
            </p>
            <p className="font-roboto mt-1">
              Updated {addResult.updated.length} stops (nothing to add on {addResult.unchanged.length}) · {addResult.uploads} files uploaded
              {addResult.reusedUploads > 0 && <> (+{addResult.reusedUploads} reused from the earlier run)</>} ·
              detail file {addResult.tail.detail ? "✓" : "✗"} · offline bump {addResult.tail.offline ? "✓" : "✗"} ·
              route {addResult.tail.route ? "regenerated (was stale)" : "unchanged"}
            </p>
            <p className="font-roboto mt-1">Audio languages on the tour now: <b>{addResult.langs.join(", ")}</b></p>
            {addResult.verify && (
              <p className="font-roboto mt-1">
                CDN check: {addResult.verify.checked}/{addResult.verify.totalFiles} manifest files reachable.
                {addResult.verify.failed.length > 0 && addResult.verify.failedNonTranscribe.length === 0 &&
                  ` ${addResult.verify.failed.length} transcript file(s) still generating — normal, they arrive in the background.`}
              </p>
            )}
            {addResult.verify && addResult.verify.failedNonTranscribe.length > 0 && (
              <div className="mt-2 text-sm text-red-700 font-mono">
                {addResult.verify.failedNonTranscribe.map((u) => <div key={u}>✗ {u}</div>)}
              </div>
            )}
          </div>
          <div className="mt-4 font-roboto">
            <p className="font-bold">Next steps:</p>
            <ol className="list-decimal ml-6 mt-1 space-y-1">
              <li>Open the Stop List and spot-check audio playback in the new languages on a few stops.</li>
              <li>In Edit Tour, make sure the tour title, description and booking email template exist in the new languages too.</li>
              <li>Transcripts for the new files generate in the background — re-run verification later to confirm.</li>
              <li>If the tour was unpublished, set Published = True in Edit Tour when happy. Publishing is never done here.</li>
            </ol>
          </div>
        </div>
      )}

      {step === "done" && (result || addResult) && (
        <div className="mt-4 flex gap-3 max-w-3xl">
          <button className={btn} onClick={() => navigate("/stopList")}>Open Stop List</button>
          <button className={btnGhost} disabled={!!busy} onClick={async () => {
            setBusy("verifying");
            try {
              const v = await verifyTour(Number(tourId), verifyLangs);
              if (isAdd && addResult) setAddResult({ ...addResult, verify: v });
              else if (result) setResult({ ...result, verify: v });
              toast.success(`Re-checked: ${v.checked - v.failed.length}/${v.totalFiles} reachable`);
            } finally { setBusy(null); }
          }}>Re-run verification</button>
        </div>
      )}
    </div>
  );
}
